import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, ChevronDown, Download, Link2, TriangleAlert } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState, type JSX, type ReactNode } from "react";

import type { ClipMatchResult, ClipSidecar } from "@shared/clipSidecar";
import { ClipMatchStats } from "@/clip/ClipStats";
import { ClipPlayer } from "@/clip/ClipPlayer";
import { ClipFetchError, clipSidecarQuery } from "@/clip/clipQuery";
import { resolveClipSource, type ClipSource, type ClipSourceError } from "@/clip/clipSource";
import { Tip } from "@/components/InfoTip";
import { buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatDate, formatDateTime, formatExpiresIn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { RELEASES_URL } from "@/lib/site";
import { clipRoute } from "@/router";

const RESULT_LABEL: Record<ClipMatchResult, string> = {
  win: "Victory",
  loss: "Defeat",
  draw: "Draw",
  remake: "Remake"
};

/**
 * Result chip colours, tuned for both themes. The app's Badge success/secondary
 * variants are dark-only (pale-200 text), which washes out on the light
 * background — so the win/draw chips get explicit light+dark text here.
 */
const RESULT_CHIP: Record<ClipMatchResult, string> = {
  win: "bg-ally/15 text-ally",
  loss: "bg-enemy/15 text-enemy",
  draw: "bg-warning/15 text-warning",
  remake: "bg-warning/15 text-warning"
};

/** The app's own strip-height spring (src/renderer/src/watch/MatchTimeline.tsx). */
const DETAILS_TRANSITION = {
  type: "spring" as const,
  stiffness: 420,
  damping: 36,
  mass: 0.7
};

const SOURCE_MESSAGE: Record<ClipSourceError, { title: string; detail: string }> = {
  missing: {
    title: "No clip in this link",
    detail:
      "This page plays a clip someone shared with you. Open the full link they sent to watch it."
  },
  bad_bucket: {
    title: "This link is malformed",
    detail: "The part of the link naming which clip to play could not be read."
  },
  bad_clip: {
    title: "This link is malformed",
    detail: "The part of the link naming which clip to play could not be read."
  }
};

export const ClipPage = (): JSX.Element => {
  const { p, c } = clipRoute.useSearch();
  const resolved = resolveClipSource(p, c);

  if (!resolved.ok) {
    const { title, detail } = SOURCE_MESSAGE[resolved.error];
    return <ClipProblem title={title} detail={detail} />;
  }
  return <ClipView source={resolved.source} />;
};

const ClipView = ({ source }: { source: ClipSource }): JSX.Element => {
  const { data, error, isPending, isFetching } = useQuery(clipSidecarQuery(source.sidecarUrl));

  // Read the clock once at mount rather than during render: Date.now() in the
  // render body is impure and yields unstable results across re-renders. The
  // load-time snapshot is all this expiry gate needs.
  const [nowMs] = useState(() => Date.now());

  // Wait out the revalidate before mounting <video> — otherwise a deleted share
  // still plays from the browser's media cache while the sidecar refetch runs.
  if (isPending || isFetching) {
    return <ClipLayout source={source} clip={undefined} loading playable={false} />;
  }

  // A gone-for-good sidecar means the share was deleted; the video will 404 too.
  if (error instanceof ClipFetchError && error.kind !== "network") {
    return (
      <ClipProblem
        title={error.kind === "forbidden" ? "This clip is private" : "This clip is gone"}
        detail={
          error.kind === "forbidden"
            ? "Its owner made it private."
            : "Its owner removed it, or the link points at a clip that never existed."
        }
      />
    );
  }

  // Honour the sidecar deadline even if R2 has not swept the objects yet.
  if (data && typeof data.expiresAtMs === "number" && data.expiresAtMs <= nowMs) {
    return (
      <ClipProblem
        title="This clip has expired"
        detail="Its share link reached the auto-delete time set by the owner."
      />
    );
  }

  // Network blip (or a pre-sidecar share): still try the video.
  return <ClipLayout source={source} clip={data} loading={false} playable />;
};

/**
 * The clip page proper, separated from fetching so it can be rendered against
 * fixture data in development without a live bucket.
 *
 * Default: one centred column. Theatre: the player goes edge-to-edge under the
 * header (YouTube-style); title and match details stay in the narrow column
 * below. Ambient blur fills any letterbox left by the clip's aspect ratio.
 */
export const ClipLayout = ({
  source,
  clip,
  loading,
  playable = true
}: {
  source: ClipSource;
  clip: ClipSidecar | undefined;
  loading: boolean;
  /** When false, show a player-sized placeholder and do not start media. */
  playable?: boolean;
}): JSX.Element => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [theatre, setTheatre] = useState(false);

  return (
    // Non-theatre: no flex-1, so `my-auto` has free space in the flex-1 <main>
    // to centre the card vertically. flex-1 + my-auto cancel out (the element
    // grows to fill, leaving nothing for the margins), which pinned it to the
    // top. Theatre stays full-bleed at the top.
    <div className={cn("flex w-full flex-col", theatre ? "flex-1" : "my-auto")}>
      <div
        className={cn(
          "w-full transition-[padding,max-width] duration-100 ease-out",
          !theatre && "mx-auto max-w-4xl px-3 pt-3 sm:px-6 sm:pt-8"
        )}
      >
        {playable ? (
          <ClipPlayer
            src={source.videoUrl}
            poster={source.posterUrl}
            title={clip?.title ?? "Shared clip"}
            knownDurationSec={clip?.durationSec ?? clip?.match?.durationSec ?? null}
            onTheatreChange={setTheatre}
          />
        ) : (
          <div className="aspect-video w-full animate-pulse rounded-xl bg-muted" aria-hidden />
        )}
      </div>

      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-3 py-4 sm:px-6 sm:py-8">
        <ClipHeadline clip={clip} loading={loading} videoUrl={source.videoUrl} />

        {clip?.match ? (
          // No `gap` between the button and the panel: the spacing lives inside
          // the collapsing element (pt-3 below) so it shrinks away with the
          // height. A parent gap would survive until unmount and then vanish in
          // one step, bumping the promo line up at the end of the animation.
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => setDetailsOpen((open) => !open)}
              aria-expanded={detailsOpen}
              className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-full border border-border bg-card/40 py-2 text-sm font-semibold transition-colors hover:bg-accent"
            >
              {detailsOpen ? "Hide match details" : "Match details"}
              <ChevronDown
                className={cn(
                  "size-4 transition-transform duration-200",
                  detailsOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence initial={false}>
              {detailsOpen ? (
                <motion.div
                  key="details"
                  // Same spring the app uses to grow its timeline strip, so the
                  // panel settles with the weight the rest of OpenVOD has.
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={DETAILS_TRANSITION}
                  className="overflow-hidden"
                >
                  {/* mt lives inside overflow-hidden (a BFC, so the margin is
                      contained) and collapses with the animated height. */}
                  <div className="mt-3 rounded-2xl border border-border bg-card/40 p-3 sm:p-4">
                    <ClipMatchStats match={clip.match} />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : null}

        <PromoLine />
      </div>
    </div>
  );
};

// --- headline ----------------------------------------------------------------

const ClipHeadline = ({
  clip,
  loading,
  videoUrl
}: {
  clip: ClipSidecar | undefined;
  loading: boolean;
  videoUrl: string;
}): JSX.Element => {
  // Read the clock once at mount (before any early return, per rules-of-hooks);
  // the expiry cutoff needn't shift mid-view, and this keeps the comparison out
  // of render (react-hooks/purity).
  const [nowMs] = useState(() => Date.now());

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
      </div>
    );
  }

  // No sidecar at all: the clip predates one, or the bucket is unreachable. The
  // video above is playing regardless, so say little and get out of the way.
  if (!clip) {
    return (
      <div className="flex items-start gap-3">
        <h1 className="flex-1 text-xl font-bold tracking-tight sm:text-2xl">Shared clip</h1>
        <ClipActions clip={undefined} videoUrl={videoUrl} />
      </div>
    );
  }

  const match = clip.match;
  const subject = match?.subject;
  // When the game was played, else when the clip was created.
  const clipDateMs = match?.playedAtMs || clip.createdAtMs;
  const expiresAtMs = clip.expiresAtMs && clip.expiresAtMs > nowMs ? clip.expiresAtMs : null;
  const hasIdentity = Boolean(match?.result || subject);

  return (
    <div className="flex items-start gap-2">
      {subject?.iconUrl ? (
        <span className="size-12 shrink-0 overflow-hidden rounded-lg bg-muted ring-1 ring-border sm:size-14">
          <img
            src={subject.iconUrl}
            alt=""
            width={56}
            height={56}
            decoding="async"
            referrerPolicy="no-referrer"
            className="size-full scale-[1.12] object-cover"
          />
        </span>
      ) : null}

      <div className="min-w-0 flex-1">
        <h1 className="text-xl leading-tight font-bold tracking-tight text-balance sm:text-2xl">
          {clip.title}
        </h1>

        {/* Result, champion, when it was played, and when it expires — one line. */}
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-muted-foreground">
          {match?.result ? (
            <span
              className={cn(
                "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                RESULT_CHIP[match.result]
              )}
            >
              {RESULT_LABEL[match.result]}
            </span>
          ) : null}
          {subject ? <span className="font-semibold text-foreground">{subject.name}</span> : null}
          {clipDateMs > 0 ? (
            hasIdentity ? (
              <MetaDot>{formatDate(clipDateMs)}</MetaDot>
            ) : (
              <span>{formatDate(clipDateMs)}</span>
            )
          ) : null}
          {expiresAtMs ? (
            <MetaDot>
              <Tip label={formatDateTime(expiresAtMs)} side="top" showCaret>
                <span className="cursor-help text-warning">
                  {formatExpiresIn(expiresAtMs, nowMs)}
                </span>
              </Tip>
            </MetaDot>
          ) : null}
        </div>
      </div>

      <ClipActions clip={clip} videoUrl={videoUrl} />
    </div>
  );
};

/** Meta item with a leading separator dot, so the row reads as one sentence. */
const MetaDot = ({ children }: { children: ReactNode }): JSX.Element => (
  <span className="flex items-center gap-2">
    <span aria-hidden className="text-muted-foreground/50">
      ·
    </span>
    <span>{children}</span>
  </span>
);

// --- actions -----------------------------------------------------------------

const ClipActions = ({
  clip,
  videoUrl
}: {
  clip: ClipSidecar | undefined;
  videoUrl: string;
}): JSX.Element => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Link copied");
    } catch {
      // Clipboard is blocked outside a secure context or without permission;
      // the address bar already holds the link, so there is nothing to recover.
    }
  };

  // Always visible, including on touch: these are the primary actions on the
  // page, so they never hide behind a hover the way desktop-only chrome might.
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <Tip label="Copy link" side="bottom">
        <button
          type="button"
          onClick={() => void copyLink()}
          aria-label="Copy link"
          className="inline-flex size-9 cursor-pointer items-center justify-center rounded-full border border-border bg-card/40 transition-colors hover:bg-accent"
        >
          {copied ? <Check className="size-4 text-success" /> : <Link2 className="size-4" />}
        </button>
      </Tip>
      <Tip label="Download" side="bottom">
        <a
          href={videoUrl}
          download={clip?.file ?? "clip.mp4"}
          aria-label="Download"
          onClick={() => toast.success("Download started")}
          className="inline-flex size-9 items-center justify-center rounded-full border border-border bg-card/40 transition-colors hover:bg-accent"
        >
          <Download className="size-4" />
        </a>
      </Tip>
    </div>
  );
};

const PromoLine = (): JSX.Element => (
  <p className="mt-2 text-center text-xs text-muted-foreground">
    Recorded with{" "}
    <a
      href={RELEASES_URL}
      target="_blank"
      rel="noreferrer noopener"
      className="font-semibold text-foreground underline-offset-4 hover:underline"
    >
      OpenVOD
    </a>
    . Free and open source.
  </p>
);

// --- failure -----------------------------------------------------------------

const ClipProblem = ({ title, detail }: { title: string; detail: string }): JSX.Element => (
  <div className="mx-auto grid w-full max-w-md flex-1 place-items-center px-6 py-20">
    <div className="text-center">
      <div className="mx-auto grid size-12 place-items-center rounded-2xl bg-muted">
        <TriangleAlert className="size-6 text-muted-foreground" />
      </div>
      <h1 className="mt-4 text-xl font-bold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{detail}</p>
      <Link to="/" className={cn(buttonVariants({ variant: "outline", size: "sm" }), "mt-6")}>
        Go to openvod.app
      </Link>
    </div>
  </div>
);
